import React from 'react';
import { useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import ModernLayout from './layout/ModernLayout';
import { PermissionProtectedRoute } from './auth/PermissionProtectedRoute';
import Properties from '../pages/properties/Properties';
import RouteGateFallback from './ui/RouteGateFallback';
import {
  isTenantSession,
  resolveTenantPortalNavigation,
} from '../utils/tenantPortalRouting';

/**
 * Root route handler that determines what to show at "/"
 * - If authenticated as landlord → Properties page (the actual root page)
 * - If authenticated as tenant → Redirect to Tenant Portal
 * - If not authenticated → LandingPage (choose tenant/landlord)
 * 
 * Note: This component is used as the index route inside a ProtectedRoute + ModernLayout wrapper
 * When not authenticated, ProtectedRoute will redirect to /login, so we handle that case separately
 */
const RootRouteHandler: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <RouteGateFallback variant="auth" />;
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isTenantSession(user)) {
    return <Navigate to={resolveTenantPortalNavigation(user)} replace />;
  }

  if (user?.role === 'LANDLORD' && user.accountStatus === 'RESTRICTED') {
    return <Navigate to="/signup-pending-approval" replace />;
  }

  // Authenticated as landlord → show Properties page with layout
  // Since ModernLayout uses <Outlet />, we use a nested Routes structure
  return (
    <ProtectedRoute>
      <Routes>
        <Route path="/" element={<ModernLayout />}>
          <Route index element={
            <PermissionProtectedRoute requiredPermission="properties">
              <Properties />
            </PermissionProtectedRoute>
          } />
        </Route>
      </Routes>
    </ProtectedRoute>
  );
};

export default RootRouteHandler;
